/**
 * FieldSyncChip (Inventory Screens Step 5.4 A-1 Gate 2) — the field zone's
 * always-visible sync-status chip, fixed bottom-right so it rides above BOTH
 * shell views (the records view had no sync surface at all before this).
 *
 * PRESENTATION ONLY: state arrives via the `counts` prop (useFieldDrain's
 * return, live since Gate 1); tap behavior is the host's (opens the
 * FieldSyncSheet). The visual + state taxonomy is lifted from the legacy
 * shared SyncChip (shared/components/SyncChip.jsx) — its useSyncStatus/
 * offlineEngine wiring is deliberately NOT reused (that chip reads the old
 * tmg-offline engine; this one is fed by the fieldDb syncQueue).
 *
 * State priority (first match wins):
 *   offline   — navigator.onLine read at render (best-effort; live
 *               connectivity reactivity arrives from OUTSIDE this file, and
 *               is real: FieldProtectedRoute listens to both online and
 *               offline (F-29's networkDown), each event sets its state, and
 *               its Provider value is a fresh object every render, so
 *               FieldZonePlaceholder — a context consumer — re-renders and
 *               this chip re-reads the global. THREE things would silently
 *               break that and restore the stale label: memoizing the
 *               Provider value, memoizing this chip, or removing the
 *               host's useFieldAuth destructure as unused.
 *   attention — conflict + not_found + error + failed > 0 (review-gated
 *               items, never dropped; resolution UX is A-Phase 2)
 *   load-failed — the `loadFailed` prop (B-224): this site's last Load
 *               failed and the rows on screen are the saved copy. An inbound
 *               fact, so its own prop and NOT a field of `counts`, which is
 *               the outbound queue.
 *   syncing   — a drain/reconcile cycle is in flight
 *   pending   — pending + saved > 0 ('saved' = saved-not-yet-reconciled is
 *               still in the pipeline — the chip must not claim Synced)
 *   unsent    — dirtyCount > 0: records hold edits that were never Saved,
 *               so they sit in NO queue (F-40 Phase 2 — this is the branch
 *               that stops 'Synced' from being a lie)
 *   synced    — queue empty, nothing unsent, online
 */

import React from 'react';
import './FieldSyncChip.css';

/* Inline SVG icons — lifted from the legacy SyncChip (presentation ref). */
function CloudIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
    </svg>
  );
}

function CloudCheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
      <path d="M9 15l2 2 4-4" />
    </svg>
  );
}

function CloudOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22.61 16.95A5 5 0 0018 10h-1.26a8 8 0 00-7.05-6M5 5a8 8 0 004 15h9a5 5 0 001.7-.3" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function CloudXIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
      <line x1="10" y1="13" x2="14" y2="17" />
      <line x1="14" y1="13" x2="10" y2="17" />
    </svg>
  );
}

export default function FieldSyncChip({ counts, loadFailed = false, onTap }) {
  const {
    pending = 0, saved = 0, conflict = 0, not_found: notFound = 0,
    error = 0, failed = 0, syncing = false, halted = false, dirtyCount = 0,
  } = counts || {};
  const attention = conflict + notFound + error + failed;
  const inFlight = pending + saved;

  let state;
  let icon;
  let label;
  if (!navigator.onLine) {
    state = 'offline';
    icon = <CloudOffIcon />;
    label = inFlight > 0 ? `Offline · ${inFlight}` : 'Offline';
  } else if (halted) {
    // Phase 2 UI arms. Positioned BELOW offline and ABOVE attention, both
    // deliberately. Below offline because a halt while the radio is down is
    // just being offline -- the rep needs no second explanation, and the
    // drain self-gates on navigator.onLine anyway. Above attention because a
    // halt leaves items at status pending with retry_count untouched, so every
    // count attention sums stays 0 and the ladder would fall straight through
    // to the bare-number pending branch -- which is precisely the state a halt
    // was indistinguishable from before this branch existed.
    // CloudXIcon is shared with attention on purpose: both mean stopped, not
    // slow. The LABEL is what separates them, and it is why this state cannot
    // render a bare count the way attention and pending do.
    state = 'halted';
    icon = <CloudXIcon />;
    label = inFlight > 0 ? `Sync stopped · ${inFlight}` : 'Sync stopped';
  } else if (attention > 0) {
    state = 'attention';
    icon = <CloudXIcon />;
    label = String(attention);
  } else if (loadFailed) {
    // B-224. BELOW attention: attention is not reassuring, and it carries a
    // count the rep acts on that this state would hide. ABOVE syncing,
    // pending, unsent and synced, every one of which can read as working
    // normally while the rows on screen are the saved copy. Offline and halted
    // stay above it: on a dead radio, Offline already explains a failed Load.
    // In-flight work rides on the label, as on offline and halted, because
    // this state hides the pending count. CloudOffIcon, as on unsent: these
    // rows did not come from the server. Colour and label tell the three
    // cloud-off states apart. "Saved copy" is the banner's own opening words,
    // so the two read as one fact; after the rep backs out to the sites list
    // the chip is the only place it shows.
    state = 'load-failed';
    icon = <CloudOffIcon />;
    label = inFlight > 0 ? `Saved copy · ${inFlight}` : 'Saved copy';
  } else if (syncing) {
    state = 'syncing';
    icon = <CloudIcon />;
    label = inFlight > 0 ? `Syncing ${inFlight}` : 'Syncing…';
  } else if (inFlight > 0) {
    state = 'pending';
    icon = <CloudIcon />;
    label = String(inFlight);
  } else if (dirtyCount > 0) {
    // F-40 Phase 2. Sits BELOW offline/attention/syncing/pending on
    // purpose — a queued item or a held one is the more urgent fact, and
    // this branch must never mask them. But it sits ABOVE 'synced',
    // because that was the lie: edits committed to IndexedDB and never
    // Saved are in no queue at all, so every count above reads 0 and the
    // chip claimed 'Synced' while the work sat on the device.
    // CloudOffIcon is reused (no new dependency): these edits have no path
    // to the server, which is nearer cloud-off than cloud-in-flight. It is
    // the offline branch's icon too, but the two are told apart by colour
    // (orange vs grey) and by the label — 'Offline' vs 'N unsent'.
    state = 'unsent';
    icon = <CloudOffIcon />;
    label = `${dirtyCount} unsent`;
  } else {
    state = 'synced';
    icon = <CloudCheckIcon />;
    label = 'Synced';
  }

  return (
    <button
      type="button"
      className={`fsc-chip fsc-chip--${state}`}
      data-testid="field-sync-chip"
      onClick={onTap}
      aria-label={`Sync status: ${label}`}
    >
      <span className="fsc-icon">{icon}</span>
      <span className="fsc-label">{label}</span>
    </button>
  );
}
